import type { ToolSection, ToolFAQ } from '../types';

/**
 * The former /astrology/kundali-matching guide (2026-09-06, CLAUDE.md J8).
 * It ranked at position 16 for calculator-shaped queries with zero clicks while
 * the calculator page it duplicated got 7 impressions, so its depth now lives
 * under the calculator and the guide URL 301s there. Kept as data so the four
 * languages stay one edit apart.
 */
export const matchingGuideSections: ToolSection[] = [
    {
      heading: 'What the Ashtakoota system measures',
      body:
        'Ashtakoota ("eight factors") compares the bride and groom across eight kootas, each carrying a fixed weight that adds up to 36 points:\n\n- **Varna (1 point)** — spiritual compatibility and ego balance.\n- **Vashya (2 points)** — mutual attraction and control in the relationship.\n- **Tara (3 points)** — health, well-being, and destiny of the couple.\n- **Yoni (4 points)** — physical and sexual compatibility.\n- **Graha Maitri (5 points)** — mental and intellectual friendship.\n- **Gana (6 points)** — temperament and behavioural compatibility.\n- **Bhakoot (7 points)** — emotional bond, family welfare, and finances.\n- **Nadi (8 points)** — health and genes of progeny; the highest-weighted factor.',
    },
    {
      heading: 'What your Guna Milan score means',
      body:
        'The 36-point total is read as a guideline, not a verdict:\n\n- **Below 18** — generally considered low; the match needs careful review.\n- **18 to 24** — acceptable and commonly considered a good match.\n- **25 to 32** — very good compatibility.\n- **33 to 36** — excellent, rare alignment.\n\nA high total can still hide a serious single-factor issue (such as Nadi or Bhakoot Dosha), and a moderate total can be perfectly workable, so the breakdown matters more than the headline number.',
    },
    {
      heading: 'Mangal Dosha (Manglik) in matching',
      body:
        'Mangal Dosha — being "Manglik" — occurs when Mars sits in certain houses of the birth chart and is traditionally associated with friction in marriage. In matching, both charts are checked: if both partners are Manglik, the dosha is often considered cancelled, and several other planetary placements can also nullify it. A Manglik status is assessed alongside Guna Milan rather than in isolation.',
    },
    {
      heading: 'Beyond the score: factors that carry extra weight',
      body:
        'Experienced astrologers weight some results more heavily than the raw total. **Nadi Dosha** (0 of 8 Nadi points) and **Bhakoot Dosha** are treated seriously because of their links to progeny, health, and emotional/financial harmony. The Dasha (planetary periods), the strength of the 7th house and its lord, and Venus and Jupiter placements are all considered before drawing a conclusion.',
    },
    {
      heading: 'How KundaliAI matches Kundalis',
      body:
        'KundaliAI performs full Guna Milan across all eight kootas, checks Mangal Dosha in both charts, and explains the planetary compatibility in plain language — so you understand not just the score, but what each factor means for the relationship. You can run a matching and ask follow-up questions in the chat.',
    },
  ];

export const matchingGuideFaq: ToolFAQ[] = [
    {
      question: 'What is a good Guna Milan score for marriage?',
      answer:
        'A Guna Milan score of 18 or more out of 36 is traditionally considered a good match. Scores of 25 to 32 indicate very good compatibility, and 33 to 36 is excellent. Below 18 is considered low and warrants a closer look at the individual factors.',
    },
    {
      question: 'Is an 18 Guna match good?',
      answer:
        'Yes — 18 out of 36 is the conventional threshold for a compatible match in Kundali matching. It is considered acceptable, though astrologers also check that no critical single factor (like Nadi or Bhakoot Dosha) is afflicted.',
    },
    {
      question: 'What happens if Nadi Dosha is present?',
      answer:
        'Nadi carries the highest weight (8 of 36 points), so Nadi Dosha — when both partners share the same Nadi — is taken seriously, traditionally linked to health and progeny concerns. However, several classical exceptions can cancel Nadi Dosha, so it should be assessed in the full context of both charts rather than treated as an automatic rejection.',
    },
    {
      question: 'Can Kundali matching be done without the birth time?',
      answer:
        'Accurate Kundali matching needs the date, time, and place of birth for both people, because the Moon sign, nakshatra, and house positions depend on the birth time. An approximate time gives an approximate result; the more precise the birth details, the more reliable the match.',
    },
    {
      question: 'Does a low Guna Milan score mean we cannot marry?',
      answer:
        'No. A low score is a signal to look more closely, not a prohibition. Many low-score matches are workable once Doshas, their cancellations, and overall chart strength are considered. KundaliAI explains which specific factors are weak so you can make an informed decision.',
    },
  ];
